import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiGetVaultsWhitelist } from "../../helpers/api";
import { AppState } from '../../store'
import { utils, BigNumber, constants } from 'ethers';
import { calculateTVL } from '../../utils/calculateTVL';
import { parseVaultDetails } from "../../utils/parseVaultDetails";

export enum ListStatus {
  Empty,
  Loading,
  Succeeded,
  Failed,
}

export interface VaultGauge {
  address: string;
  rewardToken: string;
  rewardLength: number;
  timestamp: number;
}

export interface Vault {
  name: string;
  address: string;
  gauge: VaultGauge | null;
  chainId: number;
}

interface VaultTokenInfo {
  address: string;
  symbol: string;
  decimals: number;
}

interface ILatestInfo {
  block: number
  leftover0: BigNumber
  leftover1: BigNumber
  reserves0: BigNumber
  reserves1: BigNumber
  sqrtPriceX96: BigNumber
  unclaimedFees0: BigNumber
  unclaimedFees1: BigNumber
}

export interface VaultInfo {
  chainId: number;
  name: string;
  address: string;
  feeTier: number;
  token0: VaultTokenInfo;
  token1: VaultTokenInfo;
  latestInfo: ILatestInfo;
}

interface Token {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
}

export interface VaultDetails {
  id: string;
  blockCreated: number;
  manager: string;
  address: string;
  uniswapPool: string;
  token0: Token;
  token1: Token;
  feeTier: number;
  liquidity: BigNumber;
  positionId: string;
  lowerTick: number;
  upperTick: number;
  managerFee: number;
  totalSupply: BigNumber;
  name: string;
  // Derieved fields
  tokenPrice0?: number;
  tokenPrice1?: number;
  tvl: number;
  apr: number;
  aprTimestamp: number;
  feesEarned0: BigNumber;
  feesEarned1: BigNumber;
  balance?: BigNumber;
  reserves0: BigNumber;
  reserves1: BigNumber;
  share0: BigNumber;
  share1: BigNumber;
  chainId?: number;
  reward?: BigNumber;
  rewardTokenPrice?: number;
  rewardTimestamp?: number;
}

interface VaultsByAddress {
  [address: string]: VaultDetails;
}
interface VaultsDetailsByChainID {
  [chainId: number]: VaultsByAddress
}

interface VaultsState {
  listStatus: ListStatus;
  vaultsWhitelistList: Array<Vault>;
  vaultsDetailsByChainID: VaultsDetailsByChainID;
  balancesByAddress: {
    [address: string]: BigNumber;
  },
  underlyingBalancesByAddress: {
    [address: string]: {
      balance0: BigNumber;
      balance1: BigNumber;
    };
  }
}
const initialState: VaultsState = {
  listStatus: ListStatus.Empty, // 'Empty' | 'Loading' | 'Succeeded' | 'Failed'
  vaultsWhitelistList: [],
  vaultsDetailsByChainID: {
    [1]: {},
    [10]: {},
    [137]: {},
  },
  balancesByAddress: {},
  underlyingBalancesByAddress: {},
}

export const fetchVaultsWhitelist = createAsyncThunk(
  'vaults/fetchVaultsWhitelist',
  async () => {
    const response = apiGetVaultsWhitelist();
    return response;
  }
)

const vaultsSlice = createSlice({
  name: "vaults",
  initialState,
  reducers: {
    addVaultsDetailsByChainID(state, action) {
      const { chainId, vaultsDetails }: { chainId: number; vaultsDetails: Array<VaultDetails> } = action.payload;
      vaultsDetails.forEach(vaultDetails => {
        state.vaultsDetailsByChainID[chainId][vaultDetails.address] = Object.assign({}, vaultDetails);
      })
    },
    addVaultsBalanceByAddress(state, action) {
      const { vaultAddress, balance }: { vaultAddress: string; balance: BigNumber } = action.payload;
      state.balancesByAddress[vaultAddress] = balance;
    },
    addVaultsUnderlyingBalanceByAddress(state, action) {
      const { vaultAddress, balance0, balance1 }: { vaultAddress: string; balance0: BigNumber; balance1: BigNumber; } = action.payload;
      state.underlyingBalancesByAddress[vaultAddress] = {
        balance0: balance0,
        balance1: balance1,
      };
    }
  },
  extraReducers(builder) {
    builder.addCase(fetchVaultsWhitelist.fulfilled, (state, action) => {
      state.vaultsWhitelistList = action.payload;
      state.listStatus = ListStatus.Succeeded;
    })
  }
});

export const selectVaultsList = (state: AppState) => state.vaults.vaultsWhitelistList??[];
export const selectVaultByAddress = (address: string) => (state: AppState) => state.vaults.vaultsWhitelistList.find(vault => utils.getAddress(vault.address) === utils.getAddress(address));
export const selectVaultsListByChainId = (chainId: number) => (state: AppState) => state.vaults.vaultsWhitelistList.filter(vault => vault.chainId === chainId);
export const selectVaultsListFetching = (state: AppState) => state.vaults.listStatus && state.vaults.listStatus === ListStatus.Loading;
export const selectVaultsListError = (state: AppState) => state.vaults.listStatus && state.vaults.listStatus === ListStatus.Failed;
export const selectVaultsListEmpty = (state: AppState) => state.vaults.listStatus === ListStatus.Empty;
export const selectVaultsDetailsByChainID = (chainId: number) => (state: AppState): VaultsByAddress => {
  const vaults: VaultsByAddress = {};
  Object.keys(state.vaults.vaultsDetailsByChainID[chainId]).forEach(address => {
    const vault = parseVaultDetails(Object.assign({}, state.vaults.vaultsDetailsByChainID[chainId][address]));
    vault.balance = BigNumber.from(state.vaults.balancesByAddress[vault.address] ?? 0);
    vault.reserves0 = BigNumber.from(state.vaults.underlyingBalancesByAddress[vault.address]?.balance0 ?? 0);
    vault.reserves1 = BigNumber.from(state.vaults.underlyingBalancesByAddress[vault.address]?.balance1 ?? 0);
    vault.tvl =
    calculateTVL(vault.reserves0 ?? constants.Zero, vault.token0.decimals, vault.tokenPrice0 ?? 0) +
    calculateTVL(vault.reserves1 ?? constants.Zero, vault.token1.decimals, vault.tokenPrice1 ?? 0);
    vaults[vault.address] = vault;
  });
  return vaults;
};
export const selectBalanceByAddress = (address: string) => (state: AppState) => state.vaults.balancesByAddress[address];

const { actions, reducer } = vaultsSlice;
export const { addVaultsDetailsByChainID, addVaultsBalanceByAddress, addVaultsUnderlyingBalanceByAddress } = actions;
export default reducer;